import type { Ref } from 'vue'
import { shuffle } from 'lodash-es'
import { seededShuffle } from '../../utils/array/seededShuffle'
import { isHomepageRoute } from '../../utils/url/isHomepageRoute'
import { useLocale } from '../../store/LocaleStore'
import { useFeedDeranking } from '../useFeedDeranking'
import { rejectAfter } from '../../utils/render/rejectAfter'
import type {
  Country,
  Language,
  NewsCategory,
} from '../../server/models/news/news-article'
import type { GetNewsRes } from '../../server/api/v2/news/index.types'
import type { SearchParamsNewsCategory } from '../../utils/newsCategoryTab'
import {
  registerArticleBatch,
  ensurePageviewSeed,
} from '../analytics/useFetchedContent'

export type NewsArticlesRequestsState = Partial<
  Record<NewsCategory, GetNewsRes>
>

const NEWS_ARTICLES_STATE_KEY = 'content:news:articles'

/**
 * True for the useAsyncData key of a feed batch. A server render whose only
 * failures are these can be held at the edge for a moment, because the
 * hydrating browser fetches the feed again (see the onMounted below).
 */
export const isNewsArticlesKey = (key: string) =>
  key.startsWith(`${NEWS_ARTICLES_STATE_KEY}:`)

/**
 * How long a server render waits for the feed. The content API can hang until
 * its gateway gives up, and a render that waits that long outlives the Lambda,
 * so the visitor gets an error instead of a page. Past this the render goes out
 * without the feed, held at the edge only briefly, and the browser fetches it
 * on mount.
 */
export const SERVER_FEED_TIMEOUT_MS = 3000

export const SPORTS_FEED_PERCENT = 15

const SHORT_CACHE_FEED_ENDPOINTS: Partial<Record<string, string>> = {
  'content-api-recommended': '/api/v2/news/recommended',
  'quartz-gravity': '/api/v2/news/quartz-gravity',
  'quartz-breakout': '/api/v2/news/quartz-breakout',
}

type NewsParams = {
  category: NewsCategory
  locale: Language
  country?: Country | string
  cursor: string | undefined
  sortBy: string
  contentSource?: string
  isMixedFeed?: boolean
  localNewsLocation?: string
  sportsPercent?: number
}

/**
 * Composable for fetching and managing news article headlines.
 *
 * Handles pagination, category filtering, and article deranking.
 * Also registers article batches for analytics tracking via useFetchedContent.
 *
 * @param category - Optional reactive reference to the selected news category
 * @returns Object with headlines, loading status, pagination methods, and counts
 */
// Categories whose server rendered first batch has been registered for analytics.
const hydratedBatchesRegistered = new Set<string>()

export const useNewsArticleHeadlines = (
  category?: Ref<SearchParamsNewsCategory>
) => {
  const newsArticles = useState<NewsArticlesRequestsState>(
    NEWS_ARTICLES_STATE_KEY,
    () => ({})
  )
  const siteSettingsStore = useSiteSettingsStore()
  const { data: siteSettings } = storeToRefs(siteSettingsStore)
  const sortBy = siteSettings.value.sortNewsBy ?? 'clusterSize'
  const { sortItemsByStatus } = useFeedDeranking() // Used in watch handler for deranking at fetch time
  const randomizeFirstLoad = ref(false)
  const route = useRoute()

  const selectedCategory = computed(() => category?.value || 'News')

  /**
   * Applies deranking to items at fetch time
   * Ensures sorting happens once when data arrives, not reactively
   */
  const applyDeranking = (items: NewsArticle[]): NewsArticle[] => {
    if (!siteSettings.value.enableArticleReordering) return items
    return sortItemsByStatus(
      items,
      siteSettings.value.derankItemsByStatus ?? 'none'
    )
  }

  const categoryHeadlines = computed(() => {
    const categoryHeadline = newsArticles.value?.[selectedCategory.value]
    return categoryHeadline ?? { count: 0, items: [], nextPageCursor: null }
  })
  const cursor = computed(() => categoryHeadlines.value.nextPageCursor)

  const headlines = computed(() => {
    return categoryHeadlines.value?.items ?? []
  })

  const hasMore = computed(
    () => categoryHeadlines.value.nextPageCursor !== null
  )
  const count = computed(() => categoryHeadlines.value.count)
  const { locale, country } = useLocale()

  const asyncData = useAsyncData(
    `${NEWS_ARTICLES_STATE_KEY}:${selectedCategory.value}:${cursor.value}`,
    async () => {
      // Read the request context before the first await. After an await the
      // handler is outside the Nuxt instance unless it is rewrapped, and any
      // async app hook in a plugin is enough to make that bite: the feed then
      // fails with "instance unavailable" and the page renders without it.
      const event = useRequestEvent()
      const filtersCity: unknown = useNuxtApp().$filters?.city
      const { client } = await useOpenFeature()
      const contentSourceFlag = client.getStringValue(
        'contentSource',
        'content-api-v1'
      )
      const contentSource =
        selectedCategory.value === 'Discover'
          ? 'discover-feed'
          : contentSourceFlag
      const isMixedFeed = client.getBooleanValue(
        'enableMixedArticleFeed',
        false
      )
      const sportsPercent =
        contentSource === 'quartz-feed' &&
        client.getBooleanValue('enableSportsFeed', false)
          ? SPORTS_FEED_PERCENT
          : 0
      const localNewsEnabled = client.getBooleanValue('localNewsEnabled', false)
      randomizeFirstLoad.value = client.getBooleanValue(
        'randomizeFirstLoadOrder',
        false
      )
      // The city is a per visitor header and the group key does not carry it, so
      // a rendered page must not be a local feed: the first visitor's city
      // would be served to everyone else in the group. A rendered request gets
      // the non local feed, and the browser uses the city on its own fetches.
      const rendered = Boolean(event?.context.ssrGroup)
      const userCity = rendered
        ? ''
        : ((typeof filtersCity === 'string' ? filtersCity : undefined) ??
          event?.context?.cf?.city ??
          '')
      const localNewsLocation = localNewsEnabled ? userCity.toLowerCase() : ''
      const isLocalNewsFeed =
        localNewsLocation && selectedCategory.value === 'News'

      const params: NewsParams = {
        category: selectedCategory.value,
        cursor: cursor.value || undefined,
        country,
        locale,
        sortBy,
        contentSource: contentSource ?? 'content-api-v1',
        isMixedFeed: (isMixedFeed && locale === 'en') ?? false,
        ...(isLocalNewsFeed ? { localNewsLocation } : {}),
        ...(sportsPercent > 0 ? { sportsPercent } : {}),
      }
      const endpoint =
        SHORT_CACHE_FEED_ENDPOINTS[contentSource] ?? '/api/v2/news'
      const request = $fetch<GetNewsRes>(endpoint, { params })
      if (import.meta.server) {
        return await rejectAfter(request, SERVER_FEED_TIMEOUT_MS)
      }
      return await request
    },
    {
      watch: [selectedCategory],
    }
  )
  const { data, status, error, execute } = asyncData

  const applyHeadlines = (newHeadlines: typeof data.value, seed?: number) => {
    if (!newHeadlines) return

    const previousValue = newsArticles.value[selectedCategory.value]
    const isInitialBatch = !previousValue?.items?.length
    const derankedItems = applyDeranking(newHeadlines.items)
    const shouldRandomizeOrder =
      isInitialBatch &&
      randomizeFirstLoad.value &&
      isHomepageRoute(route.path) &&
      selectedCategory.value !== 'Games'
    const newItems = shouldRandomizeOrder
      ? seededShuffle(derankedItems, seed ?? ensurePageviewSeed())
      : derankedItems
    const combinedItems = [...(previousValue?.items || []), ...newItems]

    // Register batch for analytics
    // Analytics events (NtpPage_Viewed, NtpMoreNews_Loaded) include ALL articles from each
    // API response, not just those visible in the viewport. This ensures consistent tracking
    // regardless of scroll position or which articles have rendered in the DOM.
    // - Initial batch: Used for NtpPage_Viewed event
    // - More-news batches: Used for NtpMoreNews_Loaded events
    // Skip registration for Games category — games have their own registration via useGames.
    if (selectedCategory.value !== 'Games' && import.meta.client) {
      registerArticleBatch(newItems, isInitialBatch)
    }

    newsArticles.value[selectedCategory.value] = {
      count: (previousValue?.count || 0) + newHeadlines.count,
      items: combinedItems,
      nextPageCursor: newHeadlines.nextPageCursor,
    }
  }
  watch(data, (value) => applyHeadlines(value))

  // Watchers do not run during a server render, so the server applies the
  // batch itself once the request resolves and the shared state travels to the
  // browser in the payload. Registering the batch for analytics is browser
  // work, so a hydrated first batch registers once on mount instead.
  if (import.meta.server) {
    onServerPrefetch(async () => {
      // The group seed reads the request, and the request is out of reach
      // after the await, so it is taken first and handed to the shuffle.
      const seed = ensurePageviewSeed()
      await asyncData
      applyHeadlines(data.value, seed)
    })
  } else if (isHydratingServerRender()) {
    onMounted(() => {
      // Nuxt keeps a server error through hydration instead of fetching again,
      // so a render whose feed timed out would stay empty for this visitor.
      if (error.value) {
        void execute()
        return
      }
      const category = selectedCategory.value
      if (category === 'Games' || hydratedBatchesRegistered.has(category)) {
        return
      }
      const items = newsArticles.value[category]?.items ?? []
      if (!items.length) return
      hydratedBatchesRegistered.add(category)
      // The read and viewed lists live in localStorage, so the server rendered
      // this feed un deranked and the hydrating browser kept that order,
      // silently turning deranking off for every server rendered visitor. It
      // is applied here instead, once the lists are readable.
      const deranked = applyDeranking(items)
      const current = newsArticles.value[category]
      if (current && deranked.some((item, index) => item !== items[index])) {
        newsArticles.value[category] = {
          count: current.count,
          nextPageCursor: current.nextPageCursor ?? null,
          items: deranked,
        }
      }
      registerArticleBatch(deranked, true)
    })
  }

  return {
    headlines,
    status,
    error,
    hasMore,
    count,
    // Resolves once the current request has settled, so a server render can
    // wait for the first batch before deriving anything from it. A failed
    // request settles it too, the caller only needs the wait.
    ready: asyncData.then(
      () => undefined,
      () => undefined
    ),
    fetchNextPage: async () => {
      if (!hasMore.value) return
      await execute()
    },
  }
}

export const REORDER_STRATEGIES = ['shuffleOnClick', 'moveOneOnClick'] as const

export type ReorderStrategy = (typeof REORDER_STRATEGIES)[number]

type ReorderResult = {
  category: SearchParamsNewsCategory
  count: number
}

const applyStrategy = <T>(items: T[], strategy: ReorderStrategy): T[] => {
  if (strategy === 'moveOneOnClick') {
    return [...items.slice(1), items[0]]
  }
  return shuffle(items)
}

/**
 * Reorders the news feed items for `category` in-place inside the shared Nuxt
 * `useState` store, so any consumer reading the headlines computed re-renders
 * with the new order. Returns `count: 0` (and does not mutate) when there are
 * fewer than 2 items, since a single-item array is never observably reordered.
 */
export const reorderNewsHeadlinesForCategory = (
  category: SearchParamsNewsCategory,
  strategy: ReorderStrategy
): ReorderResult => {
  const newsArticles = useState<NewsArticlesRequestsState>(
    NEWS_ARTICLES_STATE_KEY,
    () => ({})
  )
  const current = newsArticles.value[category]
  if (!current?.items || current.items.length < 2) {
    return { category, count: 0 }
  }
  newsArticles.value = {
    ...newsArticles.value,
    [category]: { ...current, items: applyStrategy(current.items, strategy) },
  }
  return { category, count: current.items.length }
}
